"use client";

import { CenterAligned, Checkbox, classNames, FlexColumn, Form, Item, ItemAvatar, ItemContent, Label, Modal, ModalBody, ModalHeaderTitle, TextBody, TextDisplay, useToasts } from "@opensea/ui-kit";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { AnimatedLogo } from "../../components/AnimatedLogo";
import { Button } from "../../components/Button";
import { useAddress, useDisconnect, useIsReady } from "../../polychain/hooks";
import { useHasAcceptedTerms } from "../../polychain/hooks/useHasAcceptedTerms";
import { useNavigation } from "../../providers/NavigationProvider";
import { useSiwe } from "../../providers/SiweProvider";
import { useTrackError } from "../../providers/TrackErrorProvider";
import { FlowActions, FlowState } from "../../stores/flowStore";
import { useAcceptTerms } from "../hooks/useAcceptTerms";
export const AcceptTermsFlow = () => {
  const t = useTranslations("wallet.AcceptTermsFlow");
  const {
    flow: currentFlow
  } = useSnapshot(FlowState);
  const address = useAddress();
  const ready = useIsReady();
  const connected = ready && Boolean(address);
  const {
    disconnect
  } = useDisconnect();
  const hasAcceptedTerms = useHasAcceptedTerms();
  const {
    renderLink: Link
  } = useNavigation();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previousFlow, setPreviousFlow] = useState(undefined);
  const {
    acceptTerms
  } = useAcceptTerms();
  const {
    promptSiwe,
    isSiwePending
  } = useSiwe();
  const {
    showErrorMessage
  } = useToasts();
  const trackError = useTrackError();
  const onContinue = async () => {
    setSubmitting(true);
    try {
      await promptSiwe();
      const {
        data,
        error: error_0
      } = await acceptTerms({
        address: address
      });
      if (data?.acceptTerms) {
        FlowActions.onSuccess();
        FlowActions.endFlow();
        if (previousFlow) {
          FlowActions.startFlow(previousFlow);
        }
      } else if (error_0) {
        trackError(error_0);
        showErrorMessage(t("errorMessage"));
      }
    } catch (error) {
      trackError(error);
      showErrorMessage(t("errorMessage"));
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    if (hasAcceptedTerms === false && currentFlow?.type !== "accept-terms" && currentFlow?.type !== "connect") {
      if (currentFlow) {
        setPreviousFlow(currentFlow);
      }
      FlowActions.startFlow({
        type: "accept-terms"
      });
    }
  }, [currentFlow, hasAcceptedTerms]);
  return <Modal className="md:rounded-xl" content={<ModalBody className="pt-12 pb-6">
          <CenterAligned className="mb-6">
            <AnimatedLogo />
          </CenterAligned>
          <TextDisplay asChild className="mb-8 text-center" size="sm">
            <ModalHeaderTitle>{t("welcomeTitle")}</ModalHeaderTitle>
          </TextDisplay>
          <FlexColumn asChild className="gap-4">
            <Form>
              <FlexColumn className={classNames("gap-4 rounded-xl border-0")}>
                <Item variant="unstyled">
                  <ItemContent>
                    <Label className="font-normal" htmlFor="agreed-to-terms">
                      <TextBody className="text-left" color="text-secondary" size="sm">
                        {t.rich("termsAgreement", {
                    termsLink: children => <Link className={termLinkClassNames} href="https://opensea.io/tos" target="_blank">
                              {children}
                            </Link>,
                    privacyLink: children_0 => <Link className={termLinkClassNames} href="https://opensea.io/privacy" target="_blank">
                              {children_0}
                            </Link>
                  })}
                      </TextBody>
                    </Label>
                  </ItemContent>
                  <ItemAvatar className="">
                    <Checkbox checked={agreedToTerms} className="size-5" id="agreed-to-terms" onClick={() => {
                setAgreedToTerms(!agreedToTerms);
              }} />
                  </ItemAvatar>
                </Item>
              </FlexColumn>

              <FlexColumn className="gap-4">
                <Button autoFocus disabled={!agreedToTerms || submitting} isLoading={submitting} onClick={onContinue} size="lg">
                  {t("continue")}
                </Button>
              </FlexColumn>
            </Form>
          </FlexColumn>
        </ModalBody>} onOpenChange={async open => {
    if (!open) {
      if (!(hasAcceptedTerms || isSiwePending)) {
        await disconnect();
      }
      FlowActions.endFlow();
      setAgreedToTerms(false);
    }
  }} open={connected && hasAcceptedTerms === false} size="sm" withCloseIcon />;
};
const termLinkClassNames = classNames("text-secondary underline", "hover:text-text-secondary/80 focus:text-text-secondary/80 active:text-text-secondary/60");